import { organisationIndexQueryKey, type OrganisationStoreResponse, useOrganisationStore } from '@jobilla/nexus-sdk'
import { useForm } from '@tanstack/react-form'
import { useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
	AuthErrorAlert,
	AuthInput,
	AuthSubmitButton,
	JobillaBrandLogo,
	logger,
	validationErrorMessages,
} from '../../ui'

interface CreateOrganizationFormProps {
	onCreated?: (organisation: OrganisationStoreResponse) => Promise<void> | void
}

export function CreateOrganizationForm({ onCreated }: CreateOrganizationFormProps) {
	const { t } = useTranslation('auth')
	const queryClient = useQueryClient()
	const busyRef = useRef(false)
	const [isCreating, setIsCreating] = useState(false)
	const [createError, setCreateError] = useState<string | null>(null)

	const createOrganizationMutation = useOrganisationStore()

	const form = useForm({
		defaultValues: {
			name: '',
		},
		onSubmit: async ({ value, formApi }) => {
			if (busyRef.current) { return }
			busyRef.current = true
			setIsCreating(true)
			setCreateError(null)

			try {
				let organisation: OrganisationStoreResponse
				try {
					organisation = await createOrganizationMutation.mutateAsync({
						data: {
							name: value.name.trim(),
						},
					})
				} catch (error) {
					logger.error(error, { category: 'organisation', data: { action: 'create' } })
					setCreateError(t('createOrganization.errorDefault'))
					return
				}

				try {
					await onCreated?.(organisation)
				} catch (error) {
					logger.error(error, { category: 'organisation', data: { action: 'open-created' } })
				}
				formApi.reset()
				await queryClient.invalidateQueries({ queryKey: organisationIndexQueryKey() })
			} finally {
				busyRef.current = false
				setIsCreating(false)
			}
		},
	})

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault()
		e.stopPropagation()
		if (busyRef.current) { return }
		void form.handleSubmit()
	}

	return (
		<div className="w-full max-w-lg mx-auto p-8">
			<JobillaBrandLogo />

			<h1 className="mt-8 text-3xl font-semibold text-gray-800 tracking-[0.01em]">
				{t('createOrganization.title')}
			</h1>
			<p className="mt-3 text-base text-gray-500">
				{t('createOrganization.description')}
			</p>

			<form onSubmit={handleSubmit} className="mt-8 space-y-8">
				<form.Field
					name="name"
					validators={{
						onChange: ({ value }) =>
							!value.trim()
								? t('createOrganization.nameRequired')
								: value.trim().length < 3
								? t('createOrganization.nameTooShort')
								: undefined,
					}}
				>
					{(field) => (
						<>
							<AuthInput
								label={t('createOrganization.nameLabel')}
								value={field.state.value}
								onChange={(val) => field.handleChange(val)}
								placeholder={t('createOrganization.namePlaceholder')}
							/>
							<AuthErrorAlert message={validationErrorMessages(field.state.meta.errors).join(',')} />
						</>
					)}
				</form.Field>

				<AuthErrorAlert message={createError} />

				<AuthSubmitButton isLoading={isCreating} disabled={isCreating}>
					{t('createOrganization.submit')}
				</AuthSubmitButton>
			</form>
		</div>
	)
}
